import React from 'react';
import {
  TableBody,
  TableCell,
  Chip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material';
import {
  ShoppingCartOutlined as CartIcon,
  ViewListOutlined as ViewListIcon,
  ViewModuleOutlined as ViewModuleIcon,
  ZoomInOutlined as ZoomInIcon,
} from '@mui/icons-material';
import ImageModal from '../../../ui/ImageModal';
import ProductImagePlaceholder from '../../../ui/ProductImagePlaceholder';
import { isUsableProductImage } from '../../../../utils/productImage';
import { SelectedProduct } from '../../../../types/sales.types';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import { linePricingDisplayBase } from '../../../../utils/sales/line-pricing.utils';
import RemoteOriginChip from '../../shared/RemoteOriginChip';
import { useProductsTableCard } from './ProductsTableCard.hooks';
import {
  StyledCard,
  StyledCardContent,
  CardHeader,
  CardTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  HeaderCell,
  StyledTableRow,
  ProductName,
  PriceText,
  TotalPriceText,
  QuantityText,
  NoDiscountText,
  ProductsGrid,
  ProductGridCard,
  ProductGridImageArea,
  ProductGridImage,
  ProductGridExpandButton,
  ProductGridContent,
} from './ProductsTableCard.styles';
import { ProductImageData } from '../../shared/ProductImagePreview';

interface ProductsTableCardProps {
  selectedProducts: SelectedProduct[];
  /** Selling branch id — used to disable delivery for remote pieces. */
  sellingLocationId?: number;
  onToggleDelivered?: (productId: number, isDelivered: boolean) => void;
}

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail || null,
  thumbnailUrl: product.thumbnailUrl || null,
  imagePath: product.imagePath || null,
});

interface ProductImageTileProps {
  product: SelectedProduct;
  sellingLocationId?: number;
  formatPrice: (price: number) => string;
  formatDiscountDisplay: (
    discountType: string,
    discountValue: number,
    originalPrice: number,
  ) => string;
  onExpand: (imageSrc: string, alt: string) => void;
}

const ProductImageTile: React.FC<ProductImageTileProps> = ({
  product,
  sellingLocationId,
  formatPrice,
  formatDiscountDisplay,
  onExpand,
}) => {
  const displayBase = linePricingDisplayBase(product);
  const productData = getProductImageData(
    product.warehouseInventoryProduct.product as ProductImageData,
  );
  const isRemote = isRemoteCartItem(product, sellingLocationId);
  const originName = product.warehouseInventoryProduct.location?.name;
  const previewSources = [
    productData.thumbnail,
    productData.thumbnailUrl,
    productData.imagePath,
  ].filter(isUsableProductImage);
  const expandedSrc =
    [productData.imagePath, productData.thumbnail, productData.thumbnailUrl].filter(
      isUsableProductImage,
    )[0] ?? '';
  const canExpand = Boolean(expandedSrc);
  const [previewIndex, setPreviewIndex] = React.useState(0);

  React.useEffect(() => {
    setPreviewIndex(0);
  }, [productData.thumbnail, productData.thumbnailUrl, productData.imagePath]);

  const thumbnailSrc = previewSources[previewIndex];
  const showPlaceholder = previewIndex >= previewSources.length;

  return (
    <ProductGridCard>
      <ProductGridImageArea
        onClick={() =>
          canExpand && onExpand(expandedSrc, product.warehouseInventoryProduct.product.name)
        }
        sx={{ cursor: canExpand ? 'zoom-in' : 'default' }}
      >
        {showPlaceholder ? (
          <ProductImagePlaceholder variant="fill" size={160} />
        ) : (
          <ProductGridImage
            src={thumbnailSrc}
            alt={product.warehouseInventoryProduct.product.name}
            loading="lazy"
            onError={() => setPreviewIndex((currentIndex) => currentIndex + 1)}
          />
        )}

        {canExpand && (
          <Tooltip title="Ver imagen completa" arrow>
            <ProductGridExpandButton
              size="small"
              aria-label={`Ver imagen completa de ${product.warehouseInventoryProduct.product.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onExpand(expandedSrc, product.warehouseInventoryProduct.product.name);
              }}
            >
              <ZoomInIcon sx={{ fontSize: 18 }} />
            </ProductGridExpandButton>
          </Tooltip>
        )}
      </ProductGridImageArea>

      <ProductGridContent>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          title={product.warehouseInventoryProduct.product.name}
        >
          {product.warehouseInventoryProduct.product.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          Código: {product.warehouseInventoryProduct.productCodeUnique || 'N/A'} · Cant. 1
        </Typography>
        {isRemote && <RemoteOriginChip locationName={originName} />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Unit. {formatPrice(displayBase)}
          </Typography>
          <Typography variant="caption" fontWeight={700}>
            Total {formatPrice(product.finalPrice)}
          </Typography>
        </Box>

        {product.markupValue > 0 || product.discountValue > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {product.markupValue > 0 && (
              <Chip
                label={`+${formatDiscountDisplay(
                  product.markupType,
                  product.markupValue,
                  displayBase,
                )}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            {product.discountValue > 0 && (
              <Chip
                label={`-${formatDiscountDisplay(
                  product.discountType,
                  product.discountValue,
                  product.adjustedPrice,
                )}`}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Sin ajuste
          </Typography>
        )}
      </ProductGridContent>
    </ProductGridCard>
  );
};

const ProductsTableCard: React.FC<ProductsTableCardProps> = ({
  selectedProducts,
  sellingLocationId,
  onToggleDelivered,
}) => {
  const { formatDiscountDisplay, formatPrice } = useProductsTableCard();
  const [viewMode, setViewMode] = React.useState<'table' | 'grid'>('table');
  const [expandedImage, setExpandedImage] = React.useState<{ src: string; alt: string } | null>(
    null,
  );
  const showDeliveryColumn = Boolean(onToggleDelivered);

  return (
    <StyledCard>
      <StyledCardContent>
        <CardHeader>
          <CardTitle variant="h6">
            <CartIcon color="primary" />
            Detalle de piezas
          </CardTitle>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode: 'table' | 'grid' | null) => {
              if (newMode) setViewMode(newMode);
            }}
            size="small"
            aria-label="vista de piezas de la venta"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(6, 182, 212, 0.12)',
                  color: '#0891b2',
                  borderColor: '#0CBB9A',
                  '&:hover': {
                    backgroundColor: 'rgba(6, 182, 212, 0.18)',
                  },
                },
              },
            }}
          >
            <ToggleButton value="table" aria-label="vista tabla">
              <Tooltip title="Vista tabla" arrow>
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="grid" aria-label="vista cuadricula">
              <Tooltip title="Vista cuadricula" arrow>
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </CardHeader>

        {viewMode === 'table' ? (
          <StyledTableContainer>
            <StyledTable>
              <StyledTableHead>
                <StyledTableRow>
                  <HeaderCell>Producto</HeaderCell>
                  <HeaderCell>Código</HeaderCell>
                  <HeaderCell align="center">Cantidad</HeaderCell>
                  <HeaderCell align="right">Precio Unit.</HeaderCell>
                  <HeaderCell align="center">Descuento</HeaderCell>
                  <HeaderCell align="right">Total</HeaderCell>
                  {showDeliveryColumn && <HeaderCell align="center">Entregada</HeaderCell>}
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {selectedProducts.map((product) => {
                  const productId = product.warehouseInventoryProduct.id;
                  const isRemote = isRemoteCartItem(product, sellingLocationId);
                  const displayBase = linePricingDisplayBase(product);
                  return (
                    <StyledTableRow key={productId}>
                      <TableCell>
                        <ProductName variant="body2">
                          {product.warehouseInventoryProduct.product.name}
                        </ProductName>
                        {isRemote && (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <RemoteOriginChip
                              locationName={product.warehouseInventoryProduct.location?.name}
                            />
                            {showDeliveryColumn && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ alignSelf: 'center' }}
                              >
                                Pendiente de traslado
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <QuantityText variant="body2">1</QuantityText>
                      </TableCell>
                      <TableCell align="right">
                        <PriceText variant="body2">{formatPrice(displayBase)}</PriceText>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          {product.markupValue > 0 && (
                            <Chip
                              label={`+${formatDiscountDisplay(
                                product.markupType,
                                product.markupValue,
                                displayBase,
                              )}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {product.discountValue > 0 ? (
                            <Chip
                              label={`-${formatDiscountDisplay(
                                product.discountType,
                                product.discountValue,
                                product.adjustedPrice,
                              )}`}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          ) : product.markupValue > 0 ? null : (
                            <NoDiscountText variant="body2">-</NoDiscountText>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <TotalPriceText variant="body2">
                          {formatPrice(product.finalPrice)}
                        </TotalPriceText>
                      </TableCell>
                      {showDeliveryColumn && (
                        <TableCell align="center">
                          <Tooltip
                            title={
                              isRemote
                                ? 'Las piezas de otras sucursales o almacén se entregan cuando lleguen'
                                : 'Marcar si el cliente se lleva esta pieza ahora'
                            }
                          >
                            <span>
                              <Switch
                                size="small"
                                checked={Boolean(product.isDelivered)}
                                disabled={isRemote}
                                onChange={(_, checked) => onToggleDelivered?.(productId, checked)}
                                inputProps={{ 'aria-label': `Entregar pieza ${productId}` }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        ) : (
          <ProductsGrid>
            {selectedProducts.map((product) => (
              <ProductImageTile
                key={product.warehouseInventoryProduct.id}
                product={product}
                sellingLocationId={sellingLocationId}
                formatPrice={formatPrice}
                formatDiscountDisplay={formatDiscountDisplay}
                onExpand={(src, alt) => setExpandedImage({ src, alt })}
              />
            ))}
          </ProductsGrid>
        )}
      </StyledCardContent>

      <ImageModal
        isOpen={Boolean(expandedImage)}
        onClose={() => setExpandedImage(null)}
        imageUrl={expandedImage?.src ?? ''}
        altText={expandedImage?.alt ?? ''}
      />
    </StyledCard>
  );
};

export default ProductsTableCard;
